import React, { useEffect } from 'react';
import { cn } from '../utils/cn';

interface PrivacyPolicyPageProps {
  theme: 'dark' | 'light';
  onGoHome: () => void;
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ 
  theme, 
  onGoHome 
}) => {
  // Автоматический скролл наверх при открытии страницы
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={cn(
      "min-h-screen",
      theme === 'dark'
        ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
        : "bg-gradient-to-br from-gray-50 via-white to-gray-100"
    )}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onGoHome}
            className={cn(
              "inline-flex items-center text-sm font-medium transition-colors mb-4",
              theme === 'dark' 
                ? "text-slate-300 hover:text-white" 
                : "text-gray-600 hover:text-gray-900"
            )}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Назад на главную
          </button>
          
          <h1 className={cn(
            "text-3xl font-bold mb-2",
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            Политика конфиденциальности
          </h1>
        </div>

        {/* Content */}
        <div className={cn(
          "prose prose-lg max-w-none",
          theme === 'dark' ? 'prose-invert' : ''
        )}>
          <div className={cn(
            "bg-white rounded-lg shadow-sm border p-8",
            theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
          )}>
            <p className={cn(
              "mb-6 leading-relaxed",
              theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
            )}>
              Настоящая Политика конфиденциальности (далее — «Политика») определяет
              порядок обработки и защиты информации о пользователях сервиса Plinus,
              расположенного по адресу: https://plinus.ru/ (далее — «Сервис»).
            </p>

            <p className={cn(
              "mb-8 leading-relaxed",
              theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
            )}>
              Оператором персональных данных является ООО «Х8», зарегистрированное по
              законодательству Российской Федерации. Контакт для обращений:
              help@plinus.ru.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                1. Общие положения
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>1.1.</strong> Настоящая Политика является неотъемлемой частью Пользовательского
                  соглашения, доступного на Сервисе.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>1.2.</strong> Используя Сервис, пользователь выражает согласие с условиями
                  настоящей Политики.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>1.3.</strong> Обработка информации осуществляется в соответствии с Федеральным
                  законом от 27 июля 2006 года № 152-ФЗ «О персональных данных».
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                2. Состав собираемой информации
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>2.1.</strong> При использовании Сервиса мы не требуем и не собираем персональные
                  данные, такие как ФИО, телефон, адрес электронной почты или место
                  жительства.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>2.2.</strong> Сервис автоматически обрабатывает только технические данные,
                  которые в ряде случаев могут трактоваться как персональные, но
                  используются нами исключительно в обезличенном виде:
                </p>
                
                <ul className={cn(
                  "list-disc list-inside ml-4 space-y-1",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <li>IP-адрес, cookies и аналогичные технологии;</li>
                  <li>данные о браузере, устройстве и языковых настройках;</li>
                  <li>статистические сведения о посещении страниц (через системы аналитики, включая Яндекс.Метрику и Google Analytics).</li>
                </ul>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>2.3.</strong> Тексты запросов, введённых пользователем для анализа ИИ, не
                  сохраняются и не используются для сторонних целей.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                3. Цели обработки информации
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>3.1.</strong> Собранные данные используются исключительно для:
                </p>
                
                <ul className={cn(
                  "list-disc list-inside ml-4 space-y-1",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <li>корректной работы Сервиса;</li>
                  <li>анализа и улучшения качества предоставляемых услуг;</li>
                  <li>статистических и маркетинговых исследований в обобщённом виде.</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                4. Передача информации третьим лицам
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>4.1.</strong> Обезличенные данные могут передаваться сервисам веб-аналитики
                  (Яндекс.Метрика, Google Analytics).
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>4.2.</strong> ООО «Х8» не передаёт никакую информацию, позволяющую напрямую
                  идентифицировать пользователя, третьим лицам, за исключением случаев,
                  прямо предусмотренных законодательством РФ.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                5. Хранение и защита информации
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>5.1.</strong> Информация хранится и обрабатывается с использованием технических и
                  организационных мер, обеспечивающих защиту от несанкционированного
                  доступа.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>5.2.</strong> Мы применяем только обезличенные данные, что исключает возможность
                  прямой идентификации пользователя.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                6. Права пользователя
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>6.1.</strong> Пользователь вправе в любой момент ограничить или отключить
                  использование cookies в настройках браузера. При этом часть функций
                  Сервиса может работать некорректно.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>6.2.</strong> Пользователь вправе направить запрос на удаление информации,
                  связанной с его обращениями или взаимодействием с Сервисом, по адресу
                  help@plinus.ru.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>6.3.</strong> Пользователь обладает иными правами, предусмотренными Федеральным
                  законом № 152-ФЗ, включая право на уточнение и блокирование персональных
                  данных, если таковые будут обрабатываться.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                7. Изменения Политики
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>7.1.</strong> ООО «Х8» вправе вносить изменения в настоящую Политику.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>7.2.</strong> Новая редакция Политики вступает в силу с момента её размещения на
                  Сервисе.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>7.3.</strong> Актуальная версия Политики всегда доступна по адресу:
                  https://plinus.ru/.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
